import { useEffect, useRef, useState } from "react";
import { usePriceCountUp } from "./useLandingMotion";

interface LandingPricingProps {
  onCreateLink: () => void;
  /**
   * Heading level for the pricing headline. Defaults to "h2", which is correct
   * inside the landing scroll where the hero already owns the page's only <h1>.
   *
   * The dedicated /pricing page passes "h1": a page with no <h1> is an SEO and
   * accessibility defect, and there the pricing headline IS the page heading.
   * Only the tag changes — the `lp-h1` class already supplies the visual style,
   * so both renderings look identical.
   */
  headingLevel?: "h1" | "h2";
}

type Cycle = "yearly" | "monthly";

// Published pricing — kept in sync with subscription_plans and the Dodo catalog.
//
// Not per seat: billing charges one flat amount per plan (DodoProvider pins quantity to 1), and
// Professional covers one person, so "/seat" would assert something the app does not do.
//
// Team is sold in two sizes because one flat price for "up to 10" charged the smallest teams the
// most per person — $19.50 each for a pair against $3.90 at ten. Two sizes put that the right way
// round: $5.80 per person at five, $4.90 at ten.
const PRO_MONTHLY = 6;
const PRO_YEARLY_PER_MONTH = 5;
const TEAM_MONTHLY = 29;
const TEAM_YEARLY_PER_MONTH = 25;
const TEAM_SEATS = 5;
// The larger Team size, surfaced as a line under the card rather than a fourth column.
const TEAM10_MONTHLY = 49;
const TEAM10_YEARLY_PER_MONTH = 41;
const TEAM10_SEATS = 10;

// Tick bubble in front of each feature. `accent` gives the Professional column its
// filled lilac badge; Free keeps the soft tint.
function Feat({ children, accent = false }: { children: React.ReactNode; accent?: boolean }) {
  return (
    <span className="lp-price-feature" style={{ display: "flex", gap: 11, alignItems: "flex-start", fontSize: 14.5, color: "var(--lp-plum-500)", fontWeight: accent ? 500 : 400 }}>
      <span
        aria-hidden="true"
        style={{
          width: 20,
          height: 20,
          flex: "none",
          marginTop: 1,
          borderRadius: "var(--lp-radius-pill)",
          background: accent ? "var(--lp-lilac)" : "var(--lp-lilac-soft)",
          color: accent ? "var(--lp-cream)" : "var(--lp-plum-700)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      </span>
      {children}
    </span>
  );
}

export function LandingPricing({ onCreateLink, headingLevel = "h2" }: LandingPricingProps) {
  const Heading = headingLevel;
  // Plan names sit one level below the section heading, so the outline has no
  // gap in either context: h2 → h3 inside the landing scroll, h1 → h2 on the
  // standalone /pricing page. Skipping a level (h1 → h3) is an accessibility
  // failure for screen-reader navigation and an SEO structure warning.
  const PlanHeading = headingLevel === "h1" ? "h2" : "h3";
  const [cycle, setCycle] = useState<Cycle>("yearly"); // yearly by default
  const yearly = cycle === "yearly";
  // Team is sold in two sizes (see the constants above) — the billing dashboard's own Team card
  // lets a subscriber toggle between them and see the price change; this card matched that only
  // in a footnote, so a shopper comparing tiers on the marketing pages never saw the 10-seat
  // price up front the way an existing customer does in-app.
  const [teamSeats, setTeamSeats] = useState<typeof TEAM_SEATS | typeof TEAM10_SEATS>(TEAM_SEATS);
  const teamMonthly = teamSeats === TEAM10_SEATS ? TEAM10_MONTHLY : TEAM_MONTHLY;
  const teamYearlyPerMonth = teamSeats === TEAM10_SEATS ? TEAM10_YEARLY_PER_MONTH : TEAM_YEARLY_PER_MONTH;
  const priceRef = useRef<HTMLSpanElement | null>(null);
  const teamPriceRef = useRef<HTMLSpanElement | null>(null);
  const animatePrice = usePriceCountUp();

  // Count the price up whenever the cycle or the chosen Team size changes (and it fires once on
  // first mount too).
  useEffect(() => {
    animatePrice(priceRef.current, yearly ? PRO_YEARLY_PER_MONTH : PRO_MONTHLY);
  }, [yearly, animatePrice]);
  useEffect(() => {
    animatePrice(teamPriceRef.current, yearly ? teamYearlyPerMonth : teamMonthly);
  }, [yearly, teamMonthly, teamYearlyPerMonth, animatePrice]);

  return (
    <section id="pricing" style={{ padding: "var(--lp-sec-y) 0", background: "var(--lp-ivory-2)" }}>
      <div className="lp-container">
        {/* Style 2a — "unified duo": both plans live inside one outer card, with the
            heading and billing toggle sharing the card's header row. */}
        <div className="lp-price-shell" data-reveal>
          <div className="lp-price-head">
            <div>
              <span className="lp-eyebrow">Pricing</span>
              <Heading className="lp-h1" style={{ marginTop: 12, maxWidth: 560 }}>
                Start free. <em>Upgrade when it earns its place.</em>
              </Heading>
              <p style={{ marginTop: 14, color: "var(--lp-plum-400)", fontSize: 13.5 }}>
                Prices in USD. No credit card to start · cancel any time.
              </p>
            </div>

            {/* Billing-cycle toggle — sliding indicator (yearly default). */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                flex: "none",
                padding: 4,
                borderRadius: "var(--lp-radius-pill)",
                background: "var(--lp-cream-2)",
                border: "1px solid var(--lp-border)",
                position: "relative",
              }}
            >
              {/* Sliding indicator sits under the selected half. Both buttons are equal-width
                  (grid 1fr 1fr) so the indicator lines up exactly and no label is clipped. */}
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  top: 4,
                  bottom: 4,
                  left: 4,
                  width: "calc(50% - 4px)",
                  borderRadius: "var(--lp-radius-pill)",
                  background: "var(--lp-plum-900)",
                  transform: yearly ? "translateX(calc(100% + 0px))" : "translateX(0)",
                  transition: "transform .4s var(--ease-spring)",
                  zIndex: 0,
                }}
              />
              {/* Segmented Monthly/Yearly control — deliberately NOT collapsed on
                  mobile: stacking the two halves would read as two buttons and
                  lose the "one of two states" affordance. */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", position: "relative", zIndex: 1 }}>
                <button
                  type="button"
                  onClick={() => setCycle("monthly")}
                  style={{ cursor: "pointer", border: "none", background: "none", fontFamily: "var(--lp-sans)", fontWeight: 600, fontSize: 13.5, padding: "9px 22px", borderRadius: "var(--lp-radius-pill)", color: yearly ? "var(--lp-plum-500)" : "var(--lp-cream)", transition: "color .3s", whiteSpace: "nowrap", textAlign: "center" }}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setCycle("yearly")}
                  style={{ cursor: "pointer", border: "none", background: "none", fontFamily: "var(--lp-sans)", fontWeight: 600, fontSize: 13.5, padding: "9px 22px", borderRadius: "var(--lp-radius-pill)", color: yearly ? "var(--lp-cream)" : "var(--lp-plum-500)", transition: "color .3s", whiteSpace: "nowrap", textAlign: "center" }}
                >
                  Yearly · Save up to 17%
                </button>
              </div>
            </div>
          </div>

          {/* Both plans are shown on mobile, stacked. They used to sit behind a
              Free/Professional tab switcher, which put a second segmented
              control directly beneath the Monthly/Yearly one — two near-identical
              pill toggles in a row, easy to mistake for each other — and hid
              half the pricing behind a tap, so the two plans could not be
              compared without switching back and forth. */}
          <div className="lp-price-plans">
            {/* Free */}
            <div id="lp-free-plan" className="lp-price-plan">
              <div className="lp-price-top">
                <PlanHeading className="lp-price-name">Free</PlanHeading>
              </div>
              <div className="lp-price-value" style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "14px 0 4px" }}>
                <span className="lp-price-amount">
                  <span className="lp-price-currency">$</span>0
                </span>
                <span style={{ fontSize: 15, color: "var(--lp-plum-400)" }}>forever</span>
              </div>
              <p className="lp-price-sub">For getting your first booking live.</p>
              {/* Empty band matching Team's seat-size toggle row, so all three cards keep the
                  same number of bands and "Create/Start ..." lines up across the row — see the
                  .lp-price-plan comment on why each card's rows must stay in lockstep. */}
              <div className="lp-team-size-row" aria-hidden="true" />
              <button
                type="button"
                className="lp-btn lp-btn-secondary"
                onClick={onCreateLink}
                style={{ display: "block", width: "100%" }}
              >
                Create your BunnyCal
              </button>
              <div className="lp-price-rule" />
              <div className="lp-price-features" style={{ display: "flex", flexDirection: "column", gap: 15 }}>
                <Feat>1 event type</Feat>
                <Feat>1 connected calendar</Feat>
                <Feat>Unlimited one-on-one bookings</Feat>
                <Feat>Held slots &amp; instant confirmations</Feat>
                <Feat>Your own booking link</Feat>
              </div>
            </div>

            {/* Professional */}
            <div id="lp-professional-plan" className="lp-price-plan lp-price-plan-pro">
              <div className="lp-price-top">
                <PlanHeading className="lp-price-name" style={{ color: "var(--lp-plum-700)" }}>Professional</PlanHeading>
              </div>
              <div className="lp-price-value" style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "14px 0 4px" }}>
                <span className="lp-price-amount">
                  <span className="lp-price-currency">$</span>
                  <span ref={priceRef}>{yearly ? PRO_YEARLY_PER_MONTH : PRO_MONTHLY}</span>
                </span>
                <span style={{ fontSize: 15, color: "var(--lp-plum-400)" }}>/month</span>
              </div>
              <p className="lp-price-sub">{yearly ? "billed yearly · save 17%" : "billed monthly"}</p>
              {/* Empty band matching Team's seat-size toggle row — see the Free card's comment. */}
              <div className="lp-team-size-row" aria-hidden="true" />
              <button
                type="button"
                className="lp-btn lp-btn-primary"
                onClick={onCreateLink}
                style={{ display: "block", width: "100%" }}
              >
                Start Professional
              </button>
              <div className="lp-price-rule" />
              <div className="lp-price-features" style={{ display: "flex", flexDirection: "column", gap: 15 }}>
                <Feat accent>Everything in Free</Feat>
                <Feat accent>Unlimited event types &amp; calendars</Feat>
                <Feat accent>Group events &amp; booking forms</Feat>
                <Feat accent>Your logo, colours &amp; brand name</Feat>
                <Feat accent>Custom booking experiences &amp; embeds</Feat>
              </div>
            </div>

            {/* Team — one price for the whole team rather than per person. Sold on capability
                you cannot buy at any size on Professional, not on being cheaper: below seven
                people it costs more than the equivalent Professional seats.

                Sized like the billing dashboard's own Team card: a real "Up to 5 / Up to 10"
                toggle that moves the headline price, not a footnote mentioning the larger size
                after the fact. A shopper comparing tiers here should see the same choice an
                existing customer sees in Billing, not a smaller version of it. */}
            <div id="lp-team-plan" className="lp-price-plan lp-price-plan-pro">
              <div className="lp-price-top">
                <PlanHeading className="lp-price-name" style={{ color: "var(--lp-plum-700)" }}>Team</PlanHeading>
                <span className="lp-price-badge">Most popular</span>
              </div>
              <div className="lp-price-value" style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "14px 0 4px" }}>
                <span className="lp-price-amount">
                  <span className="lp-price-currency">$</span>
                  <span ref={teamPriceRef}>{yearly ? teamYearlyPerMonth : teamMonthly}</span>
                </span>
                <span style={{ fontSize: 15, color: "var(--lp-plum-400)" }}>/month</span>
              </div>
              {/* The seat count is no longer said here — the toggle right below already reads
                  "Team size: Up to 5 / Up to 10", so repeating it in the sub-line was saying the
                  same thing twice. */}
              <p className="lp-price-sub">{yearly ? "billed yearly · save 14%" : "billed monthly"}</p>
              <div className="lp-team-size-row">
                <span className="lp-team-size-label">Team size</span>
                <div className="lp-team-size-seg" role="group" aria-label="Team size">
                  {/* The dark fill, slid under whichever half is active, rather than a
                      background on the button itself — so changing size reads as one object
                      moving. Decorative: the buttons' aria-pressed carries the state. */}
                  <span
                    aria-hidden="true"
                    className={teamSeats === TEAM10_SEATS ? "lp-team-size-thumb is-ten" : "lp-team-size-thumb"}
                  />
                  {([TEAM_SEATS, TEAM10_SEATS] as const).map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={teamSeats === n ? "lp-team-size-opt is-on" : "lp-team-size-opt"}
                      aria-pressed={teamSeats === n}
                      onClick={() => setTeamSeats(n)}
                    >
                      Up to {n}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="button"
                className="lp-btn lp-btn-primary"
                onClick={onCreateLink}
                style={{ display: "block", width: "100%" }}
              >
                Start Team
              </button>
              <div className="lp-price-rule" />
              <div className="lp-price-features" style={{ display: "flex", flexDirection: "column", gap: 15 }}>
                <Feat accent>Everything in Professional</Feat>
                <Feat accent>Round-robin &amp; collective events</Feat>
                <Feat accent>Team workspaces</Feat>
                <Feat accent>Remove BunnyCal branding</Feat>
              </div>
            </div>
          </div>
        </div>

        <div data-reveal style={{ marginTop: 28 }}>
          <ComparisonTable />
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- Comparison table -------------------------------- */

// value: true = included, false = not included, string = a specific value (e.g. "1", "Unlimited").
type Cell = boolean | string;
interface Row { label: string; free: Cell; pro: Cell; team: Cell }
interface Group { title: string; rows: Row[] }

// The real BunnyCal feature matrix (source of truth: PlanCatalog.java entitlements).
// Free = 1 event type, 1 calendar, one-to-one + conferencing; Professional = everything.
const COMPARISON: Group[] = [
  {
    title: "Scheduling",
    rows: [
      { label: "One-on-one event types", free: true, pro: true, team: true },
      { label: "Event types", free: "1", pro: "Unlimited", team: "Unlimited" },
      { label: "Connected calendars", free: "1", pro: "Unlimited", team: "Unlimited" },
      { label: "Your own booking link & page", free: true, pro: true, team: true },
      { label: "Held slots & instant confirmations", free: true, pro: true, team: true },
      { label: "Buffer time before & after meetings", free: true, pro: true, team: true },
      { label: "Booking forms & questionnaires", free: false, pro: true, team: true },
    ],
  },
  {
    title: "Team events",
    rows: [
      { label: "Group events (many guests, one slot)", free: false, pro: true, team: true },
      { label: "Round-robin events", free: false, pro: false, team: true },
      { label: "Collective events (multi-host)", free: false, pro: false, team: true },
      { label: "Teams", free: false, pro: false, team: `Up to ${TEAM10_SEATS}` },
    ],
  },
  {
    title: "Booking pages",
    rows: [
      { label: "Custom booking experiences", free: false, pro: true, team: true },
      { label: "Embed on your website", free: false, pro: true, team: true },
    ],
  },
  {
    title: "Your brand",
    rows: [
      { label: "Your logo, colours & brand name", free: false, pro: true, team: true },
      { label: "Custom page background & gradient", free: false, pro: true, team: true },
      // The one row where Professional is neither a yes nor a flat no: it is buyable there.
      { label: "Remove BunnyCal branding", free: false, pro: "+$3/mo", team: true },
    ],
  },
  {
    title: "Integrations",
    rows: [
      { label: "Google & Microsoft calendars", free: true, pro: true, team: true },
      { label: "Zoom, Google Meet & Microsoft Teams", free: true, pro: true, team: true },
    ],
  },
];

function Tick() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-label="Included">
      <circle cx="9" cy="9" r="8" fill="var(--lp-lilac-soft)" stroke="var(--lp-lilac)" />
      <path d="M5.5 9.2L8 11.6L12.5 6.6" stroke="var(--lp-plum-700)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function Dash() {
  return <span className="lp-cmp-dash" aria-label="Not included">—</span>;
}
function CellView({ value }: { value: Cell }) {
  if (value === true) return <Tick />;
  if (value === false) return <Dash />;
  return <span className="lp-cmp-val">{value}</span>;
}

function ComparisonTable() {
  return (
    <div className="lp-cmp" role="table" aria-label="Compare Free, Professional and Team">
      <div className="lp-cmp-head" role="row">
        <span className="lp-cmp-feature" role="columnheader">Compare plans</span>
        <span className="lp-cmp-col" role="columnheader">Free</span>
        {/* "Professional" needs ~75px but the phone column is far narrower, and .lp-cmp is
            overflow-x: hidden, so the full word would be clipped rather than scrollable. Both
            spellings sit in the DOM and CSS shows one; aria-label carries the full name so the
            column is announced properly at every width. */}
        <span className="lp-cmp-col" role="columnheader" aria-label="Professional">
          <span className="lp-cmp-col-full" aria-hidden="true">Professional</span>
          <span className="lp-cmp-col-short" aria-hidden="true">Pro</span>
        </span>
        <span className="lp-cmp-col" role="columnheader">Team</span>
      </div>
      {COMPARISON.map((group) => (
        // role="rowgroup" belongs on the element that actually contains the
        // rows. It used to sit on the title <div>, which holds only text, so
        // screen readers saw an empty rowgroup while these rows belonged to no
        // group at all. The title keeps no role and is read as plain text,
        // matching how it reads visually.
        <div className="lp-cmp-group" role="rowgroup" key={group.title}>
          <div className="lp-cmp-group-title">{group.title}</div>
          {group.rows.map((row) => (
            <div className="lp-cmp-row" role="row" key={row.label}>
              <span className="lp-cmp-feature" role="cell">{row.label}</span>
              <span className="lp-cmp-col" role="cell"><CellView value={row.free} /></span>
              <span className="lp-cmp-col" role="cell"><CellView value={row.pro} /></span>
              <span className="lp-cmp-col" role="cell"><CellView value={row.team} /></span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
